/**
 * An online race: the room a host opens and friends join with its code, the players in it, and the
 * protocol between them over a transport (`transport.ts`). Every player runs their own simulation
 * of the course with their own clock, streams their inputs to the room, and re-simulates the others
 * from theirs (`remote.ts`), so the race is a time trial with live ghosts: nothing on the network can
 * change a player's own time. `docs/online.md` describes the design.
 *
 * Messages are JSON arrays tagged by their first element; the relay adds the sender's peer number:
 *   ["h", name, band, tuningHash, commit]        who I am (on joining, and to a newcomer)
 *   ["b", band]                                  my headband changed
 *   ["c", course, key, to, round]                host course/round (to -1: a new race; else a snapshot)
 *   ["r", round]                                 the host restarted the race (a rematch)
 *   ["i", gen, firstTick, frames, to?]           my inputs (optional recipient for tape continuations)
 *   ["x", gen, tick, hash]                       my state hash at a tick, so a drift shows
 *   ["t", gen, frames, to]                       my whole run so far, for a newcomer or after a gap
 *   ["q", peer]                                  please send me your tape
 *   ["f", gen, time, falls, splits]              I finished
 * `gen` counts a player's runs (a restart, a course load starts a new one); a replica resets when it
 * changes and ignores anything older.
 */

import { cleanPlayerName } from "@lache/relay/names";
import { CLOSE, type Role, type RoomNotice, randomCode } from "@lache/relay/room";
import {
  type CourseData,
  type InputFrame,
  quantizeFrame,
  type Sim,
  sameFrame,
  type TapeFrame,
  type Tuning,
} from "@lache/sim";
import { RemotePlayer } from "./remote";
import { connectLocal, connectRelay, type Transport } from "./transport";

export interface PeerInfo {
  peer: number;
  name: string;
  band: number;
  tuningHash: string;
  commit: string;
  /** Whether a hello arrived (before it the peer is a seat, not yet a name). */
  known: boolean;
  left: boolean;
  /** The seat is reserved while its phone or connection is away. */
  away: boolean;
}

export type SessionState = "idle" | "connecting" | "hosting" | "joined" | "reconnecting" | "error";

export interface SessionHost {
  /** The local player's name and headband, read when a hello goes out. */
  name(): string;
  band(): number;
  tuningHash(): string;
  commit: string;
  tuning: Tuning;
  /** The course the race is on, or null before the host picked one. */
  course(): { name: string; data: CourseData } | null;
  /** The local player's recorded run so far (for a newcomer), or null. */
  localTape(): TapeFrame[] | null;
  /** Callbacks into the app. */
  peersChanged(): void;
  stateChanged(): void;
  courseAnnounced(name: string): void;
  restartAnnounced(): void;
  remoteFinished(peer: number): void;
  ended(reason: string): void;
  warn(text: string): void;
}

/** Ticks between input messages (30 a second at 120 Hz), and between state hashes. */
export const SEND_EVERY = 4;
export const HASH_EVERY = 240;
/** Attempts at a random code before giving up on hosting. */
const HOST_TRIES = 4;
const RECONNECT_DELAYS_MS = [250, 500, 1000, 2000, 5000];
/** Even eight maximum-width safe integers per frame fit well below the relay's 64 KiB ceiling. */
const TAPE_BATCH_FRAMES = 256;

export class OnlineSession {
  state: SessionState = "idle";
  role: Role = "guest";
  code = "";
  myPeer = -1;
  error = "";
  readonly peers = new Map<number, PeerInfo>();
  readonly remotes = new Map<number, RemotePlayer>();
  /** Messages for a peer that arrived before the course was loaded (a newcomer's first seconds). */
  private readonly held = new Map<number, string[]>();
  private transport: Transport | null = null;
  private gen = 0;
  private pending: TapeFrame[] = [];
  private pendingStart = 1;
  private hostTries = 0;
  /** A tape was asked of a peer; not asked again until it lands or the run changes. */
  private readonly asked = new Set<number>();
  private resumeToken: string | undefined;
  private connection = 0;
  private suspended = false;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private round = 0;
  private hasRound = false;
  private lastLocalTick = 0;
  /** A returning guest must see the host's current course/round before exchanging run tapes. */
  private awaitingCourseSync = false;

  constructor(
    private readonly host: SessionHost,
    private readonly relayUrl: string,
    private readonly local: boolean,
  ) {}

  get active(): boolean {
    return this.state === "hosting" || this.state === "joined" || this.state === "reconnecting";
  }

  get isHost(): boolean {
    return this.role === "host";
  }

  /** The other players, seat order. */
  others(): PeerInfo[] {
    return [...this.peers.values()].sort((a, b) => a.peer - b.peer);
  }

  /** Players still in the room (the host too), for a race's headcount. */
  present(): PeerInfo[] {
    return this.others().filter((p) => !p.left && !p.away);
  }

  /** Open a room as the host on a random code. */
  hostRoom(): void {
    this.leave();
    this.hostTries = 0;
    this.tryHost();
  }

  private tryHost(): void {
    this.hostTries++;
    this.connect(randomCode(), "host");
  }

  join(code: string): void {
    this.leave();
    this.connect(code, "guest");
  }

  private connect(code: string, role: Role): void {
    this.role = role;
    this.code = code;
    this.state = "connecting";
    this.error = "";
    this.myPeer = -1;
    this.peers.clear();
    this.dropRemotes();
    this.held.clear();
    this.asked.clear();
    this.resumeToken = undefined;
    this.suspended = false;
    this.reconnectAttempts = 0;
    this.round = 0;
    this.hasRound = false;
    this.awaitingCourseSync = false;
    this.openTransport();
  }

  private openTransport(): void {
    const connection = ++this.connection;
    const events = {
      notice: (n: RoomNotice) => {
        if (connection === this.connection) this.onNotice(n);
      },
      message: (from: number, text: string) => {
        if (connection === this.connection) this.onMessage(from, text);
      },
      closed: (closeCode: number, reason: string) => {
        if (connection === this.connection) this.onClosed(closeCode, reason);
      },
    };
    this.transport = this.local
      ? connectLocal(this.code, this.role, events)
      : connectRelay(this.relayUrl, this.code, this.role, events, this.resumeToken);
    this.host.stateChanged();
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  private scheduleReconnect(): void {
    if (this.suspended || this.reconnectTimer !== null) return;
    const delay = RECONNECT_DELAYS_MS[Math.min(this.reconnectAttempts, RECONNECT_DELAYS_MS.length - 1)]!;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.state !== "reconnecting" || this.suspended) return;
      this.reconnectAttempts = Math.min(this.reconnectAttempts + 1, RECONNECT_DELAYS_MS.length - 1);
      this.openTransport();
    }, delay);
  }

  private recover(): void {
    this.state = "reconnecting";
    this.pending = [];
    this.pendingStart = this.lastLocalTick + 1;
    this.asked.clear();
    this.held.clear();
    this.awaitingCourseSync = !this.isHost;
    this.host.stateChanged();
    this.scheduleReconnect();
  }

  /** A hidden phone gives up its socket, not its room, seat, or unfinished run. */
  suspend(): void {
    if (this.local || !this.resumeToken || !this.active) return;
    this.suspended = true;
    this.clearReconnectTimer();
    const transport = this.transport;
    this.transport = null;
    this.connection++;
    transport?.close("reconnecting");
    this.recover();
  }

  /** Visibility or network recovery retries immediately with the same private seat token. */
  resume(): void {
    if (this.local || this.state !== "reconnecting" || !this.resumeToken) return;
    this.suspended = false;
    this.reconnectAttempts = 0;
    this.clearReconnectTimer();
    // A pageshow/online pair must not create two simultaneous replacement sockets.
    if (!this.transport) this.openTransport();
  }

  /** Leave the room (or stop connecting). Quiet when idle. */
  leave(): void {
    const t = this.transport;
    const resumeToken = this.state === "reconnecting" && !this.local ? this.resumeToken : undefined;
    this.transport = null;
    this.connection++;
    this.clearReconnectTimer();
    this.resumeToken = undefined;
    this.suspended = false;
    this.awaitingCourseSync = false;
    // Idle first: the transport reports its own close, which is not the room ending on us.
    this.state = "idle";
    if (t) t.close(resumeToken ? "reconnecting" : "left");
    if (resumeToken) {
      // A reserved seat may have no open socket to carry Leave. Reclaim it only long enough to
      // deliver the explicit close; the transport's timeout bounds this best-effort notification.
      const leaving = connectRelay(
        this.relayUrl,
        this.code,
        this.role,
        {
          notice: (notice) => {
            if (notice.t === "welcome") leaving.close("left");
          },
          message: () => {},
          closed: () => {},
        },
        resumeToken,
      );
    }
    this.myPeer = -1;
    this.peers.clear();
    this.dropRemotes();
    this.held.clear();
    this.asked.clear();
    this.host.stateChanged();
  }

  private dropRemotes(): void {
    this.remotes.clear();
  }

  private send(msg: unknown[]): void {
    this.transport?.send(JSON.stringify(msg));
  }

  private hello(): void {
    this.send([
      "h",
      cleanPlayerName(this.host.name()),
      this.host.band(),
      this.host.tuningHash(),
      this.host.commit,
    ]);
  }

  setBand(band: number): void {
    if (this.active) this.send(["b", band]);
  }

  setName(): void {
    if (this.active) this.hello();
  }

  /** The host picked a course: everyone (or one newcomer) loads it. */
  announceCourse(name: string, key: string, to = -1): void {
    if (!this.isHost) return;
    if (to === -1) this.round++;
    this.send(["c", name, key, to, this.round]);
  }

  /** The host restarted the race: everyone restarts. */
  announceRestart(): void {
    if (this.isHost) this.send(["r", ++this.round]);
  }

  /**
   * The local run starts over (a restart, a course load): a new generation, the input stream from
   * tick 1 again, and every replica rebuilt on the course.
   */
  localRestart(): void {
    this.gen++;
    this.pending = [];
    this.pendingStart = 1;
    this.lastLocalTick = 0;
    this.asked.clear();
    const course = this.host.course();
    if (!course) return;
    for (const r of this.remotes.values()) r.setCourse(course.name, course.data, this.host.tuning);
    // Anything held for a peer before the course was known is theirs to replay now.
    this.consumeHeld();
  }

  private consumeHeld(): void {
    const held = [...this.held];
    this.held.clear();
    for (const [peer, texts] of held) for (const text of texts) this.onMessage(peer, text);
  }

  setTuning(tuning: Tuning): void {
    for (const r of this.remotes.values()) r.setTuning(tuning);
  }

  /** After the local simulation's tick with `input`: record it for the stream, send every few ticks. */
  afterLocalTick(input: InputFrame, sim: Sim): void {
    if (!this.active) return;
    this.lastLocalTick = sim.state.tick;
    if (this.state === "reconnecting" || this.awaitingCourseSync) {
      // The app's recorder owns this period; recovery sends its complete tape, avoiding an
      // unbounded network queue and preserving the next batch's absolute tick numbering.
      this.pending = [];
      this.pendingStart = sim.state.tick + 1;
      return;
    }
    const q = quantizeFrame(input);
    const last = this.pending[this.pending.length - 1];
    if (last && sameFrame(last, q)) last[0]++;
    else this.pending.push(q);
    const tick = sim.state.tick;
    if (tick - this.pendingStart + 1 >= SEND_EVERY) this.flush();
    if (tick % HASH_EVERY === 0) this.send(["x", this.gen, tick, sim.hash()]);
  }

  private flush(): void {
    if (this.pending.length === 0) return;
    let n = 0;
    for (const f of this.pending) n += f[0];
    this.send(["i", this.gen, this.pendingStart, this.pending]);
    this.pendingStart += n;
    this.pending = [];
  }

  localFinish(time: number, falls: number, splits: number[]): void {
    this.flush();
    this.send(["f", this.gen, time, falls, splits]);
  }

  /** Each replica takes its paced step; call once per local tick. */
  tickRemotes(): void {
    for (const [peer, r] of this.remotes) {
      const finished = r.finish !== null;
      r.pace();
      if (!finished && r.finish) this.host.remoteFinished(peer);
      const info = this.peers.get(peer);
      if (
        r.needsTape &&
        !info?.away &&
        !info?.left &&
        !this.asked.has(peer) &&
        this.state !== "reconnecting"
      ) {
        this.asked.add(peer);
        this.send(["q", peer]);
      }
      if (r.desynced) {
        r.desynced = false;
        const who = this.peers.get(peer)?.name ?? `player ${peer + 1}`;
        this.host.warn(`${who}'s run drifted from what their game shows: different tuning or build`);
      }
    }
  }

  /** Send the local run so far to one peer (a newcomer, or one that lost the stream). */
  private sendTapeTo(peer: number): void {
    if (this.awaitingCourseSync) return;
    this.flush();
    const frames = this.host.localTape();
    if (!frames) return;
    // Keep the existing tape/stream protocol: one reset followed by ordered, addressed inputs.
    // Sending a full analog-input tape in one message silently exceeds the relay's size limit.
    this.send(["t", this.gen, frames.slice(0, TAPE_BATCH_FRAMES), peer]);
    let firstTick = 1;
    for (let offset = 0; offset < frames.length; offset += TAPE_BATCH_FRAMES) {
      const batch = frames.slice(offset, offset + TAPE_BATCH_FRAMES);
      if (offset > 0) this.send(["i", this.gen, firstTick, batch, peer]);
      for (const frame of batch) firstTick += frame[0];
    }
  }

  private ensurePeer(peer: number): PeerInfo {
    let p = this.peers.get(peer);
    if (!p) {
      p = {
        peer,
        name: `Player ${peer + 1}`,
        band: peer % 8,
        tuningHash: "",
        commit: "",
        known: false,
        left: false,
        away: false,
      };
      this.peers.set(peer, p);
    }
    return p;
  }

  private ensureRemote(peer: number): RemotePlayer | null {
    const existing = this.remotes.get(peer);
    if (existing) return existing;
    const course = this.host.course();
    if (!course) return null;
    const r = new RemotePlayer(course.name, course.data, this.host.tuning);
    this.remotes.set(peer, r);
    return r;
  }

  private onNotice(n: RoomNotice): void {
    if (n.t === "welcome") {
      const returning = this.state === "reconnecting" || n.resumed === true;
      this.clearReconnectTimer();
      this.reconnectAttempts = 0;
      if (n.resumeToken) this.resumeToken = n.resumeToken;
      this.myPeer = n.peer;
      this.state = this.role === "host" ? "hosting" : "joined";
      this.error = "";
      const peers = new Set(n.peers);
      for (const p of this.peers.values()) {
        p.left = !peers.has(p.peer);
        p.away = false;
      }
      for (const peer of n.peers) {
        const p = this.ensurePeer(peer);
        p.left = false;
        p.away = n.away?.includes(peer) ?? false;
      }
      this.awaitingCourseSync = returning && !this.isHost;
      this.hello();
      if (returning && !this.awaitingCourseSync) this.exchangeTapes();
      this.host.stateChanged();
      this.host.peersChanged();
    } else if (n.t === "join") {
      const p = this.ensurePeer(n.peer);
      p.left = false;
      p.away = false;
      this.asked.delete(n.peer);
      if (!n.resumed) {
        p.known = false;
        this.remotes.delete(n.peer);
      }
      this.hello();
      // The newcomer needs the course and everyone's run so far.
      const course = this.host.course();
      if (course) {
        if (this.isHost) this.announceCourse(course.name, "", n.peer);
        this.sendTapeTo(n.peer);
        if (n.resumed) this.send(["q", n.peer]);
      }
      this.host.peersChanged();
    } else if (n.t === "away") {
      const p = this.ensurePeer(n.peer);
      p.away = true;
      this.asked.delete(n.peer);
      this.host.peersChanged();
    } else if (n.t === "leave") {
      const p = this.peers.get(n.peer);
      if (p) {
        p.left = true;
        p.away = false;
      }
      this.host.peersChanged();
    } else if (n.t === "error") {
      this.error = n.reason;
    }
  }

  /** Course precedes tapes so guests can restore replicas against the correct round. */
  private exchangeTapes(): void {
    const course = this.host.course();
    for (const p of this.present()) {
      if (this.isHost && course) this.announceCourse(course.name, "", p.peer);
      this.sendTapeTo(p.peer);
      this.send(["q", p.peer]);
    }
  }

  private onClosed(code: number, reason: string): void {
    if (this.state === "idle") return;
    const wasConnecting = this.state === "connecting";
    if (wasConnecting && code === CLOSE.ROOM_TAKEN && this.role === "host" && this.hostTries < HOST_TRIES) {
      this.transport = null;
      this.tryHost();
      return;
    }
    const active = this.active;
    this.transport = null;
    if (!this.local && this.resumeToken && active && (code < 4000 || code === 1006)) {
      this.recover();
      return;
    }
    this.clearReconnectTimer();
    this.state = wasConnecting ? "error" : "idle";
    this.error = reason || (wasConnecting ? "could not reach the race" : "");
    this.host.stateChanged();
    if (active) this.host.ended(reason || "connection lost");
  }

  private onMessage(from: number, text: string): void {
    let msg: unknown;
    try {
      msg = JSON.parse(text);
    } catch {
      return;
    }
    if (!Array.isArray(msg) || typeof msg[0] !== "string") return;
    const p = this.ensurePeer(from);
    switch (msg[0]) {
      case "h": {
        p.name = typeof msg[1] === "string" ? cleanPlayerName(msg[1]) : p.name;
        p.band = typeof msg[2] === "number" ? msg[2] : p.band;
        p.tuningHash = typeof msg[3] === "string" ? msg[3] : "";
        p.commit = typeof msg[4] === "string" ? msg[4] : "";
        const first = !p.known;
        p.known = true;
        p.left = false;
        this.host.peersChanged();
        if (first && p.tuningHash && p.tuningHash !== this.host.tuningHash())
          this.host.warn(`${p.name} plays with another tuning: their ghost may drift`);
        break;
      }
      case "b":
        if (typeof msg[1] === "number") p.band = msg[1];
        this.host.peersChanged();
        break;
      case "c":
        if (from === 0 && !this.isHost && typeof msg[1] === "string") {
          const to = typeof msg[3] === "number" ? msg[3] : -1;
          if (to === -1 || to === this.myPeer) {
            if (msg[4] !== undefined && (!Number.isSafeInteger(msg[4]) || msg[4] < 0)) break;
            const round = typeof msg[4] === "number" ? msg[4] : null;
            if (round !== null && this.hasRound && round < this.round) break;
            const sameRound = round !== null && this.hasRound && round === this.round;
            const sameCourse = this.host.course()?.name === msg[1];
            if (sameRound && !sameCourse) break;
            const unchanged = sameCourse && (sameRound || (to === this.myPeer && round === null));
            const recovering = this.awaitingCourseSync;
            this.awaitingCourseSync = false;
            if (round !== null) {
              this.round = round;
              this.hasRound = true;
            }
            if (!unchanged) this.host.courseAnnounced(msg[1]);
            this.consumeHeld();
            if (recovering) this.exchangeTapes();
          }
        }
        break;
      case "r":
        if (from === 0 && !this.isHost) {
          if (msg[1] !== undefined) {
            if (!Number.isSafeInteger(msg[1]) || msg[1] < 0 || (this.hasRound && msg[1] <= this.round)) break;
            this.round = msg[1];
            this.hasRound = true;
          }
          this.host.restartAnnounced();
        }
        break;
      case "q":
        if (msg[1] === this.myPeer) this.sendTapeTo(from);
        break;
      case "i":
      case "t":
      case "x":
      case "f": {
        if (msg[0] === "i" && msg[4] !== undefined && msg[4] !== this.myPeer) return;
        const r = this.awaitingCourseSync ? null : this.ensureRemote(from);
        if (!r) {
          const held = this.held.get(from) ?? [];
          if (held.length < 4096) held.push(text);
          this.held.set(from, held);
          return;
        }
        this.onRunMessage(from, r, msg);
        break;
      }
      default:
        break;
    }
  }

  private onRunMessage(from: number, r: RemotePlayer, msg: unknown[]): void {
    const gen = msg[1];
    if (typeof gen !== "number" || !Number.isSafeInteger(gen) || gen < 0) return;
    switch (msg[0]) {
      case "i":
        if (typeof msg[2] === "number" && Array.isArray(msg[3]))
          r.pushInputs(gen, msg[2], msg[3] as TapeFrame[]);
        break;
      case "t":
        if (gen >= r.gen && Array.isArray(msg[2]) && msg[3] === this.myPeer) {
          if (!r.loadTape(gen, msg[2] as TapeFrame[])) break;
          this.asked.delete(from);
          // A resync may restore a finish whose original message arrived before our local restart.
          if (r.finish) this.host.remoteFinished(from);
        }
        break;
      case "x":
        if (typeof msg[2] === "number" && typeof msg[3] === "string") r.expectHash(gen, msg[2], msg[3]);
        break;
      case "f":
        if (typeof msg[2] === "number" && typeof msg[3] === "number") {
          if (r.setFinish(gen, msg[2], msg[3], Array.isArray(msg[4]) ? (msg[4] as number[]) : []))
            this.host.remoteFinished(from);
        }
        break;
      default:
        break;
    }
  }
}
